import useTrustedEmailSender, {
  trustedEmailSenderState
} from 'Hooks/useTrustedEmailSender/useTrustedEmailSender'
import { evalCondition, regexEmail } from 'mailup-react-helpers'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilState, useSetRecoilState } from 'recoil'

import {
  FormElement,
  Grid,
  GridCell,
  INPUT_TEXT_VARIANT_DANGER,
  InputText,
  InputTextIcon,
  TooltipUtils,
  TrustedEmailInput
} from 'mailup-web-components'

import { pageState } from 'Pages/QuickSend/recoil/state/state'

const SenderInfo = () => {
  const intl = useIntl()
  const isWL = evalCondition('co:WHITELABEL')
  const [
    {
      pecPlusStatus,
      senderName,
      senderEmail,
      replyToEmail,
      bouncedEmail,
      fetchedSender,
      invalidReplyTo,
      waitForVerified
    },
    setPageState
  ] = useRecoilState(pageState)
  const setTrustedEmailState = useSetRecoilState(
    trustedEmailSenderState('trustedSenderQuick')
  )

  const showMessageBox = verifyResponse => {
    setPageState(prev => ({
      ...prev,
      verifyResponse,
      showVerifyResponse: true
    }))
  }

  const trustedSenderProps = useTrustedEmailSender(
    'trustedSenderQuick',
    senderEmail,
    true,
    showMessageBox
  )

  useEffect(() => {
    if (waitForVerified && trustedSenderProps.variant === 'success') {
      setPageState(prev => ({
        ...prev,
        waitForVerified: false,
        senderIsDeleted: false
      }))
    }
  }, [trustedSenderProps.variant])

  const handleChangeName = e => {
    setPageState(prev => ({
      ...prev,
      senderName: e.target.value
    }))
  }

  const handleChangeReplyTo = e => {
    setPageState(prev => ({
      ...prev,
      replyToEmail: e.target.value
    }))
    if (e.target.value.match(regexEmail)) {
      setPageState(prev => ({
        ...prev,
        invalidReplyTo: false
      }))
    } else {
      setPageState(prev => ({
        ...prev,
        invalidReplyTo: true
      }))
    }
  }

  useEffect(() => {
    if (!fetchedSender && senderEmail) {
      setTrustedEmailState(prev => ({
        ...prev,
        value: senderEmail
      }))
      setPageState(prev => ({
        ...prev,
        fetchedSender: true
      }))
    }
  }, [fetchedSender, senderEmail])

  useEffect(() => {
    fetchedSender &&
      setPageState(prev => ({
        ...prev,
        senderEmail: trustedSenderProps.value
      }))
  }, [trustedSenderProps.value])

  useEffect(() => {
    !replyToEmail &&
      setPageState(prev => ({ ...prev, replyToEmail: bouncedEmail }))
  }, [bouncedEmail])

  useEffect(() => {
    TooltipUtils.rebuild()
  }, [])

  return (
    <>
      <Grid cols={12}>
        <GridCell span={4}>
          <FormElement
            feedback={
              senderName === ''
                ? intl.formatMessage({
                    id: 'Load.EmailSendingWizard.SenderNameRequired'
                  })
                : undefined
            }
            feedbackVariant={senderName === '' ? 'danger' : undefined}
            label={intl.formatMessage({
              id: 'Load.HeaderSummary.SenderNameLabel'
            })}
            required
          >
            <InputText
              disabled={evalCondition('uo:EMAILSENDERLOCKED')}
              value={senderName}
              onChange={handleChangeName}
              variant={senderName === '' ? INPUT_TEXT_VARIANT_DANGER : null}
            />
          </FormElement>
        </GridCell>
        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.HeaderSummary.SenderEmailLabel'
            })}
            required
            labelHelp={
              pecPlusStatus
                ? intl.formatMessage({
                    id: 'Load.PecPlus.AltImgMailMittPec'
                  })
                : undefined
            }
          >
            {!pecPlusStatus ? (
              <TrustedEmailInput
                id='trustedSenderQuick'
                disabled={evalCondition('uo:EMAILSENDERLOCKED')}
                {...trustedSenderProps}
              />
            ) : (
              <InputTextIcon
                value={senderEmail}
                disabled
                tooltip={
                  isWL
                    ? intl.formatMessage({
                        id: 'Load.PecPlus.senderPecTooltipWL'
                      })
                    : intl.formatMessage({
                        id: 'Load.PecPlus.senderPecTooltip'
                      })
                }
              />
            )}
          </FormElement>
        </GridCell>
        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.HeaderSummary.ReplyToEmailLabel'
            })}
            required
            labelHelp={
              pecPlusStatus
                ? intl.formatMessage({
                    id: 'Load.PecPlus.replyToPecTooltip'
                  })
                : undefined
            }
            feedback={
              replyToEmail === ''
                ? intl.formatMessage({
                    id: 'Load.EmailSendingWizard.SenderNameRequired'
                  })
                : replyToEmail !== '' && invalidReplyTo
                ? intl.formatMessage({
                    id: 'Load.Common.WrongFormatWarning'
                  })
                : undefined
            }
            feedbackVariant={senderName === '' ? 'danger' : undefined}
          >
            {!pecPlusStatus ? (
              <InputText
                value={replyToEmail}
                onChange={handleChangeReplyTo}
                disabled={evalCondition('uo:EMAILSENDERLOCKED')}
                variant={
                  senderName === '' || invalidReplyTo
                    ? INPUT_TEXT_VARIANT_DANGER
                    : null
                }
              />
            ) : (
              <InputTextIcon value={replyToEmail} disabled />
            )}
          </FormElement>
        </GridCell>
      </Grid>
    </>
  )
}

export default SenderInfo
